import { Button } from '@up2stay/up2promo-ui';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';

import type { WorkspaceIntent } from './onboarding-intent';
import { ONBOARDING_LAYOUT, onboardingSpring } from './onboarding-motion';

interface OnboardingSuccessStepProps {
  mode: 'onboarding' | 'create';
  workspaceName: string;
  onGoToDashboard: () => void;
  intent?: WorkspaceIntent | null;
  /** Cap native resume: gate persist still in flight. */
  ctaDisabled?: boolean;
  /** Cap native: plan step skipped (web-only billing) — explain Free start. */
  showNativePlanNotice?: boolean;
}

export function OnboardingSuccessStep({
  mode,
  workspaceName,
  onGoToDashboard,
  intent = null,
  ctaDisabled = false,
  showNativePlanNotice = false,
}: OnboardingSuccessStepProps) {
  const dict = useDictionary();
  const reduceMotion = useReducedMotion();

  const title =
    mode === 'create'
      ? dict.onboarding.success.createMode.title
      : dict.onboarding.success.title;

  const intentKey = intent ?? 'full';
  const nextHint =
    mode === 'create'
      ? null
      : (dict.onboarding.success.nextHintByIntent[
          intentKey as keyof typeof dict.onboarding.success.nextHintByIntent
        ] ?? dict.onboarding.success.nextHint);

  const displayName = workspaceName.trim() || dict.workspace.workspace;

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <div className="flex w-full max-w-md flex-col items-center gap-2 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
          {dict.onboarding.progress.ready}
        </p>

        <h2 className="text-balance text-[22px] font-bold leading-tight text-foreground sm:text-[24px]">
          {title}
        </h2>

        <motion.p
          layoutId={reduceMotion ? undefined : ONBOARDING_LAYOUT.workspaceName}
          transition={onboardingSpring}
          className="max-w-prose text-pretty text-[15.5px] font-bold leading-tight text-foreground"
        >
          {displayName}
        </motion.p>

        {nextHint ? (
          <p className="mt-4 text-[13.5px] font-semibold text-foreground">
            {nextHint}
          </p>
        ) : null}

        {/* Informational only — no link/CTA to external purchase (App Store 3.1.3). */}
        {showNativePlanNotice ? (
          <p
            className="mt-2 max-w-prose text-pretty text-[13px] text-muted-foreground"
            data-testid="onboarding-native-plan-notice"
          >
            {dict.onboarding.success.nativePlanNotice}
          </p>
        ) : null}

        <Button
          onClick={onGoToDashboard}
          variant="primary"
          size="lg"
          className="mt-3 w-full max-w-xs"
          disabled={ctaDisabled}
        >
          {dict.onboarding.success.goToDashboard}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
